import { createClient } from "@supabase/supabase-js";
import { VITE_PUBLIC_SUPABASE_URL, VITE_PUBLIC_SUPABASE_ANON_KEY } from "../utils/env";
import * as Sentry from "@sentry/react";
import type { Account, AppVersion } from "../utils/types";
import type { Doctor, InsightsMetric } from "@happy-doc/shared-types";


const supabaseUrl = VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = VITE_PUBLIC_SUPABASE_ANON_KEY;

/**
 * Supabase response types -----------------------------
 */
export type SupabaseResponse<T> =
	| { data: T; error: null }
	| { data: null; error: Error };


export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Error handling wrapper function
export async function retryWrapper<T>(
	func: () => Promise<{ data: T | null; error: any }>
): Promise<{ data: T | null; error: any }> {
	let retries = 0;
	const maxRetries = 3;
	let delay = 100; // Initial delay in ms

	while (retries < maxRetries) {
		try {
			const { data, error } = await func();

			if (error) {
				// Handle AbortError (code 20), PGRST116 (no rows returned), and fetch aborts
				if (error?.code === '20' || error?.code === "PGRST116" || (error?.message && error?.message?.includes('AbortError'))) {
					// Do nothing if error is due to aborting the fetch
					return { data: null, error: null };
				}

				// Handle JWT expired
				if (error?.message && isUnAuthorizedError(error)) {
					// Try to refresh session
					const { error: refreshError } = await supabase.auth.refreshSession();
					if (refreshError) {
						// Can't refresh session, redirect to login
						await supabase.auth.signOut();
						window.location.href = '/';
						return { data: null, error };
					} else {
						// Session refreshed, retry the operation
						continue;
					}
				}

				// Handle supabase network errors
				if (error?.message && isNetworkError(error)) {
					// Retry with exponential backoff
					throw error; // Throw to catch block
				}

				// For other errors, log and rethrow
				if (isFatalError(error)) {
					Sentry.captureException(error);
					return { data: null, error };
				}
			}

			// If no error, return data
			return { data, error: null };
		} catch (error) {
			retries++;
			if (retries >= maxRetries) {
				// Max retries reached
				if (!isNetworkError(error)) {
					Sentry.captureException(error);
				}
				return { data: null, error };
			}
			// Exponential backoff
			await new Promise((res) => setTimeout(res, delay));
			delay *= 2; // Exponential backoff
		}
	}
	return { data: null, error: null };
}

function isNetworkError(error: any): boolean {
	return error?.message?.toLowerCase().includes('failed to fetch')
		|| error?.message?.toLowerCase().includes('load failed')
		|| error?.message?.toLowerCase().includes('network error')
		|| error?.message?.toLowerCase().includes('timeout')
		|| error?.message?.toLowerCase().includes('abort')
		|| error?.message?.toLowerCase().includes("network request failed")
}

function isUnAuthorizedError(error: any): boolean {
	return (
		error?.message?.toLowerCase().includes("unauthorized") ||
		error?.message?.toLowerCase().includes("jwt expired") ||
		error?.message?.toLowerCase().includes("jwt invalid") ||
		error?.message?.toLowerCase().includes("jwt malformed") ||
		error?.message?.toLowerCase().includes("jwt expired") ||
		error?.message?.toLowerCase().includes("401") ||
		error?.message?.toLowerCase().includes("403")
	);
}

function isFatalError(error: any): boolean {
	// Define fatal errors
	// For example, if error code is not network related
	const nonFatalErrors = ['NetworkError', 'TimeoutError'];
	if (nonFatalErrors.includes(error.code)) {
		return false;
	}
	return true;
}

// APP VERSIONS
export const getAppVersion = async (signal: AbortSignal): Promise<AppVersion | null> => {

	const { data, error } = await supabase.from("app_versions").select("id, version").abortSignal(signal).eq("id", 1).single()

	if (error) {
		return null;
	}

	return data as unknown as AppVersion || null;

};

// ACCOUNT DATA
export const fetchAccountData = async (id: string, abortSignal: AbortSignal): Promise<Account | null> => {
	const { data, error } = await retryWrapper(async () => {
		return await supabase
			.from("account")
			.select(`
			clinic_name, 
			clinic_location,
			clinic_time_zone, 
			id, 
			is_self_serve, 
			feature_flags, 
			created_at, 
			meta_data, 
			api_key, 
			current_pims, 
			hidden_documents,
			trial_end_date,
			has_completed_profile
			`)
			.abortSignal(abortSignal)
			.eq("id", id)
			.limit(1)
			.single()
	});

	if (error) {
		return null;
	}

	return data
};

type AccountHierarchyResponse = {
	target_account_id: string;
	account: Account;
};

export const getChildAccounts = async (id: string, abortSignal: AbortSignal): Promise<Account[]> => {
	const { data, error } = await retryWrapper(async () => {
		return await supabase
			.from("account_hierarchy")
			.select(`
				parent_account_id,
				target_account_id,
				account:target_account_id (
					id,
					clinic_name,
					clinic_location,
					clinic_time_zone,
					is_self_serve,
					feature_flags,
					created_at,
					status
				)
			`)
			.eq("is_active", true)
			.eq("parent_account_id", id)
			.abortSignal(abortSignal)
	});

	if (error) {
		Sentry.captureException(error);
		return [];
	}

	return (data as AccountHierarchyResponse[] || []).map(item => item.account);
};

// DOCTORS
export const getSupabaseDoctors = async (abortSignal: AbortSignal): Promise<Doctor[]> => {
	const { data, error } = await retryWrapper<Doctor[]>(async () => {
		return await supabase
			.from('doctors')
			.select(`
				id, 
				doctor_name, 
				created_at, 
				email, 
				email_confirmed_at,
				confirmation_sent_at,
				account_id,
				sort_index,
				pms_employee_id,
				pms_export_id,
				is_archived,
				doctor_document_types (document_type_id, doctor_id)
				`)
			.abortSignal(abortSignal)
			.order('created_at', { ascending: true });
	});

	if (error) {
		return [];
	}

	return data || [];
};

// METRIC COMPONENTS

/*
* Fetches the insights sharing data for the current account.
* If there are account specific insights, they will be returned.
* If there are no account specific insights, the default insights will be returned.
* @returns an array of insights sharing data
*/
export const getMetricComponents = async (abortSignal: AbortSignal): Promise<InsightsMetric[]> => {

	const { data, error } = await retryWrapper(async () => {
		return await supabase
			.from("insights_sharing")
			.select(`
				target_account_id, 
				insights_metric_id,
				...insights_metrics (
					id,
					metric_key,
					name,
					is_premium
				)
			`)
			.abortSignal(abortSignal)
	});

	if (error) {
		return [];
	}
	// find account specific metrics
	const accountSpecificMetrics = data?.filter((d: any) => d?.target_account_id !== null) ?? [];

	// if there are account specific metrics, use them without any of the default metrics
	let metricComponents: InsightsMetric[] = [];
	if (accountSpecificMetrics.length > 0) {
		metricComponents = accountSpecificMetrics.map((d: any) => {
			return {
				id: d?.insights_metric_id,
				metricKey: d?.metric_key,
				name: d?.name,
				isPremium: d?.is_premium,
			}
		});
	}

	// use the default metrics
	const defaultMetrics = data?.filter((d: any) => d?.target_account_id === null) ?? [];
	metricComponents.push(...defaultMetrics.map((d: any) => {
		return {
			id: d?.insights_metric_id,
			metricKey: d?.metric_key,
			name: d?.name,
			isPremium: d?.is_premium,
		}
	}));

	// make sure there are no duplicates between metric account specific and default metrics
	const uniqueMetricComponents = metricComponents.filter((metric, index, self) =>
		index === self.findIndex((t) => t.id === metric.id)
	);

	return uniqueMetricComponents;

}

/*
* Fetches all metrics shared with an account, embedding their category and its parent.
* @param abortSignal - The abort signal to cancel the request.
* @returns A promise that resolves to an array of metrics with their category path.
*/
export const getSharedMetricsWithCategories = async (
	abortSignal: AbortSignal
): Promise<InsightsMetric[]> => {

	// 1. Get the IDs of all metrics shared with the account.
	const metrics = await getMetricComponents(abortSignal);

	const metricIds = metrics.map(item => item.id);

	if (metricIds.length === 0) {
		return [];
	}

	// 2. Fetch the full metric details, embedding their category and the category's parent.
	const { data: metricsData, error: metricsError } = await supabase
		.from('insights_metrics')
		.select(`
            *,
            insights_metric_categories (
                *,
                parent: parent_id (*)
            )
        `)
		.in('id', metricIds)
		.order('display_order', { ascending: true })
		.abortSignal(abortSignal);

	if (metricsError) {
		Sentry.captureException(metricsError);
		return [];
	}


	// transform the data to be in the correct format
	const transformedData = metricsData.map((metric) => {
		return {
			...metric,
			metricKey: metric.metric_key,
			isPremium: metric.is_premium,
			insightMetricCategories: metric.insights_metric_categories,
		}
	})

	return transformedData;
}
